import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import globalUserProfile from '../../../../stores/global-user-profile';
import { LectureLecturer } from '../../../../api/types/lecturers/lectures/lecture-lecturer';
import { ProfessorProfile } from '../../../../api/types/settings/settings-all';
import { UserType } from '../../../../api/types/user/user-type';
import { StartLectureAttendanceDialog } from '../../../../pages/course/lectures/list-view/components/start-lecture-attendance-dialog';
import { EditTextDialog } from '../../../../pages/settings/componenets/edit-entity-name-dialog/edit-text-dialog';
import { updateLectureNameProfessorOperation } from './operations/update-lecture-name-professor-operation';
import { MyLectureStartDialog } from './components/my-lecture-start-dialog';
import { MyLectureFinishDialog } from './components/my-lecture-finish-dialog';
import { MyLectureActions } from './components/my-lecture-actions';
import { getCommonColumns } from './components/my-lecture-columns';

export const MyLectures = observer(() => {
  const [showUpdateAttendanceDialog, setShowUpdateAttendanceDialog] =
    useState(false);
  const [selectedLectureForUpdate, setSelectedLectureForUpdate] =
    useState<LectureLecturer | null>(null);
  const [showStartConfirmDialog, setShowStartConfirmDialog] = useState(false);
  const [selectedLectureForStart, setSelectedLectureForStart] =
    useState<LectureLecturer | null>(null);
  const [isStartingLecture, setIsStartingLecture] = useState(false);
  const [showFinishConfirmDialog, setShowFinishConfirmDialog] = useState(false);
  const [selectedLectureForFinish, setSelectedLectureForFinish] =
    useState<LectureLecturer | null>(null);
  const [isFinishingLecture, setIsFinishingLecture] = useState(false);
  const [editLectureNameDialogId, setEditLectureNameDialogId] = useState<
    number | undefined
  >(undefined);
  const [editLectureName, setEditLectureName] = useState<string>('');
  const [selectedLectureForEdit, setSelectedLectureForEdit] =
    useState<LectureLecturer | null>(null);
  const [isUpdatingLectureName, setIsUpdatingLectureName] = useState(false);

  // Get the professor's lectures from the global user profile
  const getLectures = (): LectureLecturer[] => {
    if (!globalUserProfile.userProfile) return [];

    if (globalUserProfile.userProfile.userType === UserType.LECTURER) {
      const professorProfile = globalUserProfile.userProfile
        .profile as ProfessorProfile;
      return professorProfile.lectures || [];
    }

    return [];
  };

  const allLectures = getLectures();

  // Helper function to check if a date is today
  const isToday = (date: Date): boolean => {
    const today = new Date();
    const targetDate = new Date(date);
    return (
      today.getFullYear() === targetDate.getFullYear() &&
      today.getMonth() === targetDate.getMonth() &&
      today.getDate() === targetDate.getDate()
    );
  };

  // Helper function to check if a date is in the past (not today)
  const isPast = (date: Date): boolean => {
    const today = new Date();
    const todayStart = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );
    return new Date(date) < todayStart;
  };

  // Helper function to check if a date is in the future (not today)
  const isFuture = (date: Date): boolean => {
    const today = new Date();
    const todayEnd = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + 1,
    );
    return new Date(date) >= todayEnd;
  };

  // Filter lectures by categories
  const getTodayLectures = (): LectureLecturer[] => {
    return allLectures.filter((lecture) =>
      isToday(new Date(lecture.startTime)),
    );
  };

  const getPastLectures = (): LectureLecturer[] => {
    return allLectures
      .filter((lecture) => {
        const lectureDate = new Date(lecture.startTime);
        return (
          isPast(lectureDate) &&
          lecture.actualStartTime !== null &&
          lecture.actualEndTime !== null
        );
      })
      .sort(
        (a, b) =>
          new Date(b.startTime).getTime() - new Date(a.startTime).getTime(),
      );
  };

  const getIrregularLectures = (): LectureLecturer[] => {
    return allLectures
      .filter((lecture) => {
        const lectureDate = new Date(lecture.startTime);
        const isNotToday = !isToday(lectureDate);

        return (
          (isPast(lectureDate) && lecture.actualStartTime === null) ||
          (lecture.actualStartTime !== null &&
            lecture.actualEndTime === null &&
            isNotToday)
        );
      })
      .sort(
        (a, b) =>
          new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
      );
  };

  const getFutureLectures = (): LectureLecturer[] => {
    return allLectures
      .filter((lecture) => isFuture(new Date(lecture.startTime)))
      .sort(
        (a, b) =>
          new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
      );
  };

  const todayLectures = getTodayLectures();
  const pastLectures = getPastLectures();
  const irregularLectures = getIrregularLectures();
  const futureLectures = getFutureLectures();

  // Template for status indicator (for today's lectures)
  const statusTemplate = (rowData: LectureLecturer) => {
    if (rowData.actualStartTime !== null && rowData.actualEndTime !== null) {
      return <Tag severity="success" value="הסתיימה" />;
    } else if (rowData.actualStartTime !== null) {
      return <Tag severity="warning" value="החלה" />;
    }
    return <Tag severity="info" value="טרם החלה" />;
  };

  // Handle start lecture - open confirmation dialog first
  const handleStartLecture = (lecture: LectureLecturer) => {
    setSelectedLectureForStart(lecture);
    setShowStartConfirmDialog(true);
  };

  // Handle finish lecture - open confirmation dialog first
  const handleFinishLecture = (lecture: LectureLecturer) => {
    setSelectedLectureForFinish(lecture);
    setShowFinishConfirmDialog(true);
  };

  // Handle update attendance - open dialog
  const handleUpdateAttendance = (lecture: LectureLecturer) => {
    setSelectedLectureForUpdate(lecture);
    setShowUpdateAttendanceDialog(true);
  };

  // Handle edit lecture name - open dialog
  const handleEditLectureName = (lecture: LectureLecturer) => {
    setSelectedLectureForEdit(lecture);
    setEditLectureName(lecture.name || '');
    setEditLectureNameDialogId(lecture.id);
  };

  // Handle save lecture name
  const handleSaveLectureName = async (id: number, newName: string) => {
    if (!selectedLectureForEdit) return;

    await updateLectureNameProfessorOperation(
      selectedLectureForEdit.programId,
      selectedLectureForEdit.courseId,
      selectedLectureForEdit.id,
      newName,
      setIsUpdatingLectureName,
    );

    setEditLectureNameDialogId(undefined);
    setSelectedLectureForEdit(null);
    setEditLectureName('');
  };

  // Handle hide edit dialog
  const handleHideEditDialog = () => {
    setEditLectureNameDialogId(undefined);
    setSelectedLectureForEdit(null);
    setEditLectureName('');
  };

  // Template for action buttons (only for today's lectures)
  const actionTemplate = (rowData: LectureLecturer) => {
    return (
      <MyLectureActions
        lecture={rowData}
        onStartLecture={handleStartLecture}
        onFinishLecture={handleFinishLecture}
        onUpdateAttendance={handleUpdateAttendance}
      />
    );
  };

  // Column configuration props
  const columnProps = {
    onEditLectureName: handleEditLectureName,
  };

  // Column configuration props for past lectures (no edit allowed)
  const pastLecturesColumnProps = {
    onEditLectureName: handleEditLectureName,
    allowEditName: false,
  };

  return (
    <div style={{ padding: '20px', width: '100%' }}>
      {/* Start Lecture Confirmation Dialog */}
      <MyLectureStartDialog
        visible={showStartConfirmDialog}
        selectedLecture={selectedLectureForStart}
        isLoading={isStartingLecture}
        setIsLoading={setIsStartingLecture}
        onHide={() => {
          setShowStartConfirmDialog(false);
          setSelectedLectureForStart(null);
        }}
      />

      {/* Finish Lecture Confirmation Dialog */}
      <MyLectureFinishDialog
        visible={showFinishConfirmDialog}
        selectedLecture={selectedLectureForFinish}
        isLoading={isFinishingLecture}
        setIsLoading={setIsFinishingLecture}
        onHide={() => {
          setShowFinishConfirmDialog(false);
          setSelectedLectureForFinish(null);
        }}
      />

      {/* Edit Lecture Name Dialog */}
      <EditTextDialog
        id={editLectureNameDialogId}
        value={editLectureName || ' '}
        title="עריכת שם המפגש"
        label="נא להזין את שם המפגש"
        loading={isUpdatingLectureName}
        onSave={handleSaveLectureName}
        onHide={handleHideEditDialog}
      />

      {/* Update Attendance Dialog */}
      <StartLectureAttendanceDialog
        visible={showUpdateAttendanceDialog}
        lecture={null}
        lectureInfo={
          selectedLectureForUpdate
            ? {
                programId: selectedLectureForUpdate.programId,
                courseId: selectedLectureForUpdate.courseId,
                lectureId: selectedLectureForUpdate.id,
              }
            : undefined
        }
        useProfessorMode={true}
        updateAttendanceOnly={true}
        onHide={() => {
          setShowUpdateAttendanceDialog(false);
          setSelectedLectureForUpdate(null);
        }}
      />

      {/* Today's Lectures Table */}
      <div
        style={{
          color: '#041E49',
          fontSize: '30px',
          fontWeight: 'bold',
          marginBottom: '20px',
        }}
      >
        הרצאות היום ({todayLectures.length})
      </div>

      <DataTable
        value={todayLectures}
        emptyMessage={<div style={{ textAlign: 'right' }}>אין הרצאות היום</div>}
        rowHover
        dataKey="id"
        scrollable
        scrollHeight="300px"
        tableStyle={{
          direction: 'rtl',
        }}
        className="p-datatable-striped"
        style={{ marginBottom: '40px' }}
      >
        {getCommonColumns(columnProps, false)}
        <Column
          header="סטטוס"
          body={statusTemplate}
          style={{ textAlign: 'center', minWidth: '100px' }}
          headerStyle={{ textAlign: 'center' }}
        />
        <Column
          header="פעולות"
          body={actionTemplate}
          style={{ textAlign: 'center', minWidth: '150px' }}
          headerStyle={{ textAlign: 'center' }}
        />
      </DataTable>

      {/* Past Lectures Table */}
      <div
        style={{
          color: '#041E49',
          fontSize: '30px',
          fontWeight: 'bold',
          marginBottom: '20px',
        }}
      >
        הרצאות שקרו ({pastLectures.length})
      </div>

      <DataTable
        value={pastLectures}
        emptyMessage={
          <div style={{ textAlign: 'right' }}>אין הרצאות קודמות</div>
        }
        rowHover
        dataKey="id"
        scrollable
        scrollHeight="300px"
        tableStyle={{
          direction: 'rtl',
        }}
        className="p-datatable-striped"
        style={{ marginBottom: '40px' }}
      >
        {getCommonColumns(pastLecturesColumnProps)}
      </DataTable>

      {/* Irregular Lectures Table */}
      <div
        style={{
          color: '#041E49',
          fontSize: '30px',
          fontWeight: 'bold',
          marginBottom: '20px',
        }}
      >
        הרצאות חריגות ({irregularLectures.length})
      </div>

      <DataTable
        value={irregularLectures}
        emptyMessage={
          <div style={{ textAlign: 'right' }}>אין הרצאות חריגות</div>
        }
        rowHover
        dataKey="id"
        scrollable
        scrollHeight="300px"
        tableStyle={{
          direction: 'rtl',
        }}
        className="p-datatable-striped"
        style={{ marginBottom: '40px' }}
      >
        {getCommonColumns(columnProps)}
      </DataTable>

      {/* Future Lectures Table */}
      <div
        style={{
          color: '#041E49',
          fontSize: '30px',
          fontWeight: 'bold',
          marginBottom: '20px',
        }}
      >
        הרצאות עתידיות ({futureLectures.length})
      </div>

      <DataTable
        value={futureLectures}
        emptyMessage={
          <div style={{ textAlign: 'right' }}>אין הרצאות עתידיות</div>
        }
        rowHover
        dataKey="id"
        scrollable
        scrollHeight="400px"
        tableStyle={{
          direction: 'rtl',
        }}
        className="p-datatable-striped"
      >
        {getCommonColumns(columnProps)}
      </DataTable>
    </div>
  );
});
